import type {
  Affix,
  AffixId,
  DieForgeMarks,
  DieSides,
  DieSpellId,
  EnemyTypeId,
  LoadedFaces,
  GameState,
  GameStats,
  MasterDie,
  Phase,
  Rarity,
  WorldRef,
} from './types';
import { sha256Hex } from './hash';
import { BALANCE } from '../data/balance';
import { AFFIX_BY_ID, affixAppliesTo } from '../data/affixes';
import { DIE_SPELL_BY_ID } from '../data/dieSpells';
import { NODES } from '../data/nodes';
import { MACHINES, RARITY_ORDER } from '../data/machines';
import { isMaterialId } from '../data/materials';
import { initialNodes } from './skilltree';
import { isBossId, isValidWorld } from './world';

/**
 * Version du format de sauvegarde.
 *
 * **2** depuis la v0.5.0 (COMMENTS v0.4.1 / arbitrage A8) : la suppression du
 * prestige et la refonte de l'arbre rendent les parties v1 illisibles. Une
 * sauvegarde de version < 2 est **rejetée** (`normalizeSave` renvoie `null`) et
 * remplacée par une partie neuve ; `core/storage.ts` le signale à l'interface.
 *
 * v0.7.0 : la version **reste 2**. Tout ce que COMMENTS v0.6.0 ajoute est
 * **additif** et se complète par une valeur par défaut — `meta.minionEnabled`
 * (false), `stats.playSeconds` (0), `MasterDie.loaded` (absent), les nouveaux
 * nœuds (niveau 0). Une partie v0.6.x se recharge donc telle quelle.
 *
 * v0.8.0 : la version **reste 2** (§F). Tout ce que le bestiaire ajoute est un
 * **état de combat**, jamais persisté : effet de l'ennemi courant, assommement
 * (`state.stunRemaining`), vitesse du chrono, bonification de PV du prochain
 * ennemi, Expédition perdue et abandon (`state.combat.*`). Le format ne les
 * connaît pas ; une sauvegarde faite pendant un combat revenant de toute façon
 * en Accalmie, ils repartent tous neutres au chargement (constructeur de
 * `Game`, puis `phases.resetCombat` au combat suivant). Une sauvegarde v0.7.x
 * se recharge donc telle quelle.
 *
 * v0.9.0 : la version **reste 2** (DECISIONS §G, A33). `stats.loreSeen` est
 * **additif** — voir `normalizeStats` pour la règle de migration d'une
 * sauvegarde qui ne le connaît pas encore.
 *
 * v0.26.0 : la version **reste 2** (A72). Tout est additif, avec défauts : les
 * quinze nouveaux nœuds (niveau 0), les dés d20 (`sides: 20`) et leurs affixes
 * nommés, et les compteurs de run `run.critStreak`, `run.spellStreak`,
 * `run.backhoe`, `run.backhoeArmed` et `run.guinsoo` (0, `false`, `{}`). Une
 * sauvegarde v0.25.x se recharge donc telle quelle.
 *
 * v0.42.0 : la version **reste 2** (A88). Les séries `run.critStreak` et
 * `run.spellStreak` ne sont plus écrites : elles vivent dans `state.combat`
 * (remises à zéro à chaque combat depuis A77), jamais sauvegardées, comme le
 * journal. Une sauvegarde qui les porte encore se recharge, les champs sont
 * ignorés.
 *
 * v0.38.0 : la version **reste 2** (phase 5, A78, A82). Mode infini et
 * crédits, tout est additif : `run.world` (monde du niveau infini, `null`
 * sinon ou s'il est illisible), `run.bossId` (boss tiré, `null` sinon) et
 * `stats.creditsSeen` (`false` par défaut : c'est ce qui déclenche la victoire
 * différée d'une partie déjà au-delà du niveau 10). La phase `victory` est
 * conservée au rechargement, comme `runLost`.
 */
export const SAVE_VERSION = 2;

/**
 * v0.44.0 (A87) : la version **reste 2**. `meta.saveId` (identifiant de la
 * partie, posé à « Nouvelle partie ») et `meta.tainted` (partie marquée par le
 * debug, collant) sont **additifs** : une sauvegarde qui ne les porte pas en
 * reçoit un `saveId` neuf au chargement (la campagne en cours compte, aucune
 * remise à zéro) et `tainted: false`.
 *
 * v0.45 (A93) : la version **reste 2** ; la relecture suffit au retrait de la
 * *GachaDice v100*, de *Centésimal* et d'*Avant-garde* — voir
 * `retiredNodesRefund` et `normalizeDie` (d100 → d20).
 *
 * v0.46.0 (A94) : la version **reste 2**. Tout est additif : les dix nœuds de
 * l'Abîme (niveau 0), les champs de dé `level`, `material`, `enriched` et
 * `forge` (absents sur un dé de campagne, écrits seulement s'ils existent) et
 * `meta.corruptedPurchases` (`{}`). La *Relance* en attente
 * (`state.forgePending`) n'est jamais écrite : un rechargement vaut « garder ».
 *
 * v0.55.0 (A112) : la version **reste 2**. `run.gateReached` (boss du dernier
 * niveau gratuit battu sans achat) est additif, `false` par défaut, et la
 * phase `gate` (écran d'achat) est conservée au rechargement. L'état
 * « acheté » (`state.entitled`) n'est **jamais** écrit : une sauvegarde
 * exportée ne débloque rien.
 */
export const SAVE_KEY = 'diceordie.save.v1';

export interface SaveData {
  version: number;
  meta: {
    gold: number;
    savedAt: number;
    runGold: number;
    /** *Larbin* actif ou non (A18) — additif, `false` par défaut. */
    minionEnabled: boolean;
    /** Identifiant de la partie (UUID), posé à « Nouvelle partie » (additif A87). */
    saveId: string;
    /** Partie marquée par le debug, collant (additif A87, `false` par défaut). */
    tainted: boolean;
    /**
     * *Corrupted GachaDice* : dés achetés par machine, compteur permanent
     * (additif A94-13, `{}`). Optionnel dans le type pour les sauvegardes
     * construites à la main (`tools/simLib.ts`).
     */
    corruptedPurchases?: Record<string, number>;
  };
  run: {
    phase: Phase;
    level: number;
    day: number;
    // Champs additifs v0.26.0 : **optionnels** dans le type (un appelant qui
    // construit une sauvegarde à la main — `tools/simLib.ts` — n'a pas à les
    // connaître), toujours renseignés par `normalizeSave` / `buildSave`.
    /** *Tractopelle* : dés Basiques en réserve (additif, 0, sans plafond). */
    backhoe?: number;
    /** Lancer de tractopelle armé (additif, `false`). */
    backhoeArmed?: boolean;
    /** *Guinsoo* : compteur de lancers par identifiant de dé (additif, `{}`). */
    guinsoo?: Record<string, number>;
    /** Monde du niveau infini (additif v0.38.0, `null`). */
    world?: WorldRef | null;
    /** Boss de l'Invasion du niveau infini (additif v0.38.0, `null`). */
    bossId?: EnemyTypeId | null;
    /** Mur payant : boss du dernier niveau gratuit battu sans achat (additif v0.55.0, A112, `false`). */
    gateReached?: boolean;
  };
  inventory: MasterDie[];
  /** Identifiant de dé par emplacement (`null` = vide). */
  equipped: (string | null)[];
  tree: Record<string, number>;
  stats: GameStats;
  rngSeed: { seed: number; state: number };
}

export function emptyStats(): GameStats {
  return {
    runs: 0,
    bestLevel: 1,
    totalGold: 0,
    totalKills: 0,
    bestThrow: 0,
    daysPlayed: 0,
    throws: 0,
    playSeconds: 0,
    loreSeen: false,
    creditsSeen: false,
  };
}

// ─────────────────────────────────────────────────────────── Validation

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function int(v: unknown, fallback: number, min = -Infinity): number {
  const n = Math.floor(num(v, fallback));
  return Math.max(min, n);
}

const PHASES: Phase[] = [
  'title',
  'expedition',
  'expeditionEnd',
  'accalmie',
  'invasion',
  'invasionWon',
  'runLost',
  'victory',
  'gate',
];

/**
 * Les phases de combat ne sont pas reprenables : une sauvegarde faite pendant
 * une Expédition ou une Invasion revient en Accalmie (DECISIONS D).
 *
 * `runLost` est **conservée** : c'est l'acquittement de la défaite qui remet la
 * run à zéro (or et dés perdus — COMMENTS v0.4.1 §1.3). La ramener en Accalmie
 * laisserait une run morte avec son or (DECISIONS §D.16).
 *
 * `victory` (phase 5, A82) est conservée de même : une partie rechargée
 * pendant l'écran de victoire le retrouve, et c'est son acquittement qui fait
 * passer au niveau 11.
 *
 * `gate` (écran d'achat, A112) est conservée elle aussi : une partie rechargée
 * devant le mur payant le retrouve.
 */
function normalizePhase(v: unknown): Phase {
  const p = typeof v === 'string' && (PHASES as string[]).includes(v) ? (v as Phase) : 'title';
  if (p === 'title' || p === 'accalmie' || p === 'runLost' || p === 'victory' || p === 'gate') return p;
  return 'accalmie';
}

function normalizeAffixes(v: unknown): Affix[] {
  if (!Array.isArray(v)) return [];
  const out: Affix[] = [];
  for (const raw of v) {
    if (!isObject(raw)) continue;
    const id = raw.id;
    if (typeof id !== 'string' || !(id in AFFIX_BY_ID)) continue;
    const def = AFFIX_BY_ID[id as AffixId];
    const value = Math.min(def.max, Math.max(def.min, num(raw.value, def.min)));
    out.push({ id: id as AffixId, value });
  }
  return out;
}

/** Sorts d'un dé sauvegardé : identifiants inconnus et doublons écartés. */
function normalizeSpells(v: unknown): DieSpellId[] {
  if (!Array.isArray(v)) return [];
  const out: DieSpellId[] = [];
  for (const raw of v) {
    if (typeof raw !== 'string') continue;
    const id = raw as DieSpellId;
    if (DIE_SPELL_BY_ID[id] && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * *Pipé* sauvegardé (A20) : la face cible et les faces remplacées sont relues
 * telles quelles si elles tiennent dans le dé, sinon le champ est abandonné —
 * un dé Pipé illisible redevient un dé normal plutôt qu'un dé impossible.
 */
function normalizeLoaded(v: unknown, sides: number): LoadedFaces | undefined {
  if (!isObject(v)) return undefined;
  const face = Math.floor(num(v.face, 0));
  if (face < 1 || face > sides) return undefined;
  if (!Array.isArray(v.replaced)) return undefined;
  const replaced: number[] = [];
  for (const raw of v.replaced) {
    const f = Math.floor(num(raw, 0));
    if (f < 1 || f > sides || f === face || replaced.includes(f)) continue;
    replaced.push(f);
  }
  if (replaced.length === 0) return undefined;
  return { face, replaced };
}

/**
 * v0.45 (A93) : nœuds **retirés** du jeu, avec leur coût d'A89 — *Centésimal*
 * (machine de d100, 60 000 or, un niveau) et *Avant-garde* (20 000 or, cinq
 * niveaux), progression `× 1,55` par niveau (`nodeCostGrowth` de l'époque,
 * figée ici : l'or rendu est celui qui a été payé).
 */
export const RETIRED_NODES: Readonly<Record<string, { baseCost: number; maxLevel: number }>> = {
  shop_v100: { baseCost: 60000, maxLevel: 1 },
  ars_vanguard: { baseCost: 20000, maxLevel: 5 },
};
const RETIRED_NODE_COST_GROWTH = 1.55;

/**
 * Or investi dans les nœuds retirés (A93) d'un arbre sauvegardé brut :
 * `Σ round(coûtBase × 1,55^i)` pour chaque niveau possédé, comme `nodeInvested`.
 * Ces nœuds disparaissent de l'arbre relu (`normalizeTree` ne lit que `NODES`) ;
 * `normalizeSave` rend cet or au joueur.
 */
export function retiredNodesRefund(tree: unknown): number {
  if (!isObject(tree)) return 0;
  let total = 0;
  for (const [id, def] of Object.entries(RETIRED_NODES)) {
    const level = Math.min(def.maxLevel, int(tree[id], 0, 0));
    for (let i = 0; i < level; i++) {
      total += Math.round(def.baseCost * Math.pow(RETIRED_NODE_COST_GROWTH, i));
    }
  }
  return total;
}

/** Forme d'arrivée d'un d100 retiré (A93). */
const RETIRED_D100_TARGET: DieSides = 20;

/** L'affixe `id` est-il permis sur un dé à `sides` faces (`minSides ≤ sides ≤ maxSides`) ? */
function affixAllowedOn(id: AffixId, sides: number): boolean {
  const def = AFFIX_BY_ID[id];
  return !!def && sides >= def.minSides && (def.maxSides === undefined || sides <= def.maxSides);
}

/**
 * `retireD100` (A93) : un dé à 100 faces relu devient un **d20** — rareté,
 * sorts, nom et affixes conservés, sauf un affixe que le d20 n'autorise pas
 * (bornes `minSides` / `maxSides`), retiré ; ses éventuelles faces pipées sont
 * relues sur 20 faces. Sans ce drapeau, le d100 est relu tel quel.
 */
function normalizeDie(v: unknown, index: number, retireD100 = false): MasterDie | null {
  if (!isObject(v)) return null;
  const id = typeof v.id === 'string' && v.id.length > 0 ? v.id : `d_restored_${index}`;
  // v0.43.0 (A89-1) : un `sides: 100` est valide ; toute autre valeur inconnue
  // retombe sur 4, comme avant. v0.45 (A93) : d100 → d20 si `retireD100`.
  const retired = retireD100 && v.sides === 100;
  const sides: DieSides = retired
    ? RETIRED_D100_TARGET
    : v.sides === 100
      ? 100
      : v.sides === 20
        ? 20
        : v.sides === 12
          ? 12
          : v.sides === 6
            ? 6
            : 4;
  const rarity: Rarity =
    typeof v.rarity === 'string' && (RARITY_ORDER as string[]).includes(v.rarity)
      ? (v.rarity as Rarity)
      : 'common';
  const read = normalizeAffixes(v.affixes);
  const affixes = retired ? read.filter((a) => affixAllowedOn(a.id, sides)) : read;
  const name = typeof v.name === 'string' && v.name.trim() ? v.name : 'Dé inconnu';
  // L'ordre des champs suit `createMasterDie` : un export JSON doit être
  // identique avant et après un aller-retour de sauvegarde.
  const die: MasterDie = { id, sides, rarity, affixes, spells: normalizeSpells(v.spells), name };
  // *Pipé* jamais sur un d100 (A89-4) : des faces pipées relues sur un d100
  // (sauvegarde modifiée) sont écartées, le dé se joue sans l'effet.
  const loaded =
    affixes.some((a) => a.id === 'loaded') && affixAppliesTo('loaded', sides)
      ? normalizeLoaded(v.loaded, sides)
      : undefined;
  if (loaded) {
    // `loaded` est posé **avant** `name`, comme dans `createMasterDie`.
    return withInfiniteFields({ id, sides, rarity, affixes, spells: die.spells, loaded, name }, v);
  }
  return withInfiniteFields(die, v);
}

/** Plafond de relecture du niveau d'un dé (20 + 2 × 10 niveaux de Marteau, avec marge). */
const SAVE_DIE_LEVEL_MAX = 60;

/** Niveau de dé relu : entier de 1 à 60, sinon absent (A94-5). */
function normalizeDieLevel(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= SAVE_DIE_LEVEL_MAX ? v : undefined;
}

/**
 * Marques de forge relues (A94) : compteurs entiers ≥ 0, index d'affixe
 * bornés par le nombre d'affixes du dé (sinon écartés), `fused` seulement
 * s'il vaut `true`. Un champ qui n'est pas un objet est abandonné.
 */
function normalizeForgeMarks(v: unknown, affixCount: number): DieForgeMarks | undefined {
  if (!isObject(v)) return undefined;
  const slot = (raw: unknown): number | undefined =>
    typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw < affixCount ? raw : undefined;
  const marks: DieForgeMarks = {
    rerolls: int(v.rerolls, 0, 0),
    tempers: int(v.tempers, 0, 0),
    transmutes: int(v.transmutes, 0, 0),
  };
  const rerollSlot = slot(v.rerollSlot);
  if (rerollSlot !== undefined) marks.rerollSlot = rerollSlot;
  if (v.fused === true) marks.fused = true;
  const absolvedSlot = slot(v.absolvedSlot);
  if (absolvedSlot !== undefined) marks.absolvedSlot = absolvedSlot;
  return marks;
}

/**
 * Champs de mode infini d'un dé (A94) — niveau, Matériau, Enrichi, Corrupted,
 * marques de forge — relus et ajoutés **après** `name`, dans l'ordre où le jeu
 * les pose. Un dé de campagne n'en reçoit aucun.
 *
 * v0.49.0 (A98-6) : `corrupted` booléen relu tel quel ; **absent**, un dé qui
 * porte `level`, `material` ou `enriched` est relu Corrupted — avant la
 * v0.49.0, seules les *Corrupted GachaDice* (et la Fusion, qui en part)
 * posaient ces champs.
 */
function withInfiniteFields(die: MasterDie, v: Record<string, unknown>): MasterDie {
  const level = normalizeDieLevel(v.level);
  if (level !== undefined) die.level = level;
  if (isMaterialId(v.material)) die.material = v.material;
  if (typeof v.enriched === 'boolean') die.enriched = v.enriched;
  if (typeof v.corrupted === 'boolean') die.corrupted = v.corrupted;
  else if (level !== undefined || die.material !== undefined || die.enriched !== undefined) die.corrupted = true;
  const forge = normalizeForgeMarks(v.forge, die.affixes.length);
  if (forge) die.forge = forge;
  // A122-8 : favori relu seulement s'il vaut `true` (absent sinon), tout dé.
  if (v.favorite === true) die.favorite = true;
  return die;
}

/** Compteurs des *Corrupted GachaDice* relus : machines `corrupted` connues, entiers ≥ 0. */
function normalizeCorruptedPurchases(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isObject(v)) return out;
  for (const m of MACHINES) {
    if (!m.corrupted || v[m.id] === undefined) continue;
    const n = int(v[m.id], 0, 0);
    if (n > 0) out[m.id] = n;
  }
  return out;
}

function normalizeTree(v: unknown): Record<string, number> {
  const tree = initialNodes();
  if (!isObject(v)) return tree;
  for (const def of NODES) {
    const level = int(v[def.id], tree[def.id], 0);
    tree[def.id] = Math.min(def.maxLevel, level);
  }
  // La racine est toujours possédée.
  tree.root = Math.max(1, tree.root);
  return tree;
}

function normalizeStats(v: unknown): GameStats {
  const base = emptyStats();
  if (!isObject(v)) return base;
  return {
    runs: int(v.runs, base.runs, 0),
    bestLevel: Math.max(1, int(v.bestLevel, base.bestLevel, 1)),
    totalGold: int(v.totalGold, base.totalGold, 0),
    totalKills: int(v.totalKills, base.totalKills, 0),
    bestThrow: Math.max(0, num(v.bestThrow, base.bestThrow)),
    daysPlayed: int(v.daysPlayed, base.daysPlayed, 0),
    throws: int(v.throws, base.throws, 0),
    // Chrono de partie (COMMENTS v0.6.0 §2) : champ **additif**, 0 par défaut
    // sur une sauvegarde v0.6.x — la version de sauvegarde ne change pas.
    playSeconds: Math.max(0, num(v.playSeconds, base.playSeconds)),
    // Lore d'ouverture (A33) : champ **additif**. Absent (sauvegarde < v0.9.0)
    // → `true` si la partie a déjà joué un jour ou une run (le lore ne doit
    // pas surgir après coup), `false` sinon (partie jamais lancée).
    loreSeen:
      typeof v.loreSeen === 'boolean'
        ? v.loreSeen
        : int(v.daysPlayed, base.daysPlayed, 0) > 0 || int(v.runs, base.runs, 0) > 0,
    // Crédits (phase 5, A78, A82) : champ **additif**, `false` par défaut —
    // une partie déjà au-delà du niveau 10 aura sa victoire différée.
    creditsSeen: v.creditsSeen === true,
  };
}

/** Monde sauvegardé : recopié s'il est valide (`isValidWorld`), sinon `null`. */
function normalizeWorld(v: unknown): WorldRef | null {
  if (!isValidWorld(v)) return null;
  return v.kind === 'zone' ? { kind: 'zone', zone: v.zone } : { kind: 'procedural', seed: v.seed };
}

/**
 * Compteurs de *Guinsoo* sauvegardés : seuls les dés connus de l'inventaire,
 * valeurs entières bornées à 0..2 (au 3e lancer le compteur repart à 0).
 */
function normalizeGuinsoo(v: unknown, known: ReadonlySet<string>): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isObject(v)) return out;
  for (const [id, raw] of Object.entries(v)) {
    if (!known.has(id)) continue;
    const n = Math.min(2, int(raw, 0, 0));
    if (n > 0) out[id] = n;
  }
  return out;
}

/** UUID aléatoire d'une partie (`crypto.randomUUID`, sinon `getRandomValues`, sinon `Math.random`). */
export function makeSaveId(): string {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return `save-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
  }
}

/** Un `saveId` relu : chaîne non vide de 8 à 64 caractères sûrs, sinon `null`. */
function readSaveId(v: unknown): string | null {
  return typeof v === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(v) ? v : null;
}

/** Sauvegarde vierge (nouvelle partie). */
export function emptySave(seed = 1): SaveData {
  return {
    version: SAVE_VERSION,
    meta: {
      gold: 0,
      savedAt: 0,
      runGold: 0,
      minionEnabled: false,
      saveId: makeSaveId(),
      tainted: false,
      corruptedPurchases: {},
    },
    run: {
      phase: 'title',
      level: 1,
      day: 1,
      backhoe: 0,
      backhoeArmed: false,
      guinsoo: {},
      world: null,
      bossId: null,
      gateReached: false,
    },
    inventory: [],
    equipped: [null],
    tree: initialNodes(),
    stats: emptyStats(),
    rngSeed: { seed, state: seed },
  };
}

/**
 * Valide une sauvegarde venue de `localStorage` (ou d'un import de debug) et
 * complète tous les champs manquants par leurs valeurs par défaut.
 *
 * Ne jette jamais, mais **rejette** (`null`) :
 *  - ce qui n'est pas un objet ;
 *  - toute sauvegarde de version **< 2** — arbitrage A8 : la suppression du
 *    prestige et la refonte de l'arbre rendent les parties v1 inexploitables,
 *    elles sont effacées et remplacées par une partie neuve, avec un message
 *    sur l'écran titre (`core/storage.ts`).
 */
export function normalizeSave(raw: unknown): SaveData | null {
  const base = emptySave();
  if (!isObject(raw)) return null;
  if (int(raw.version, 0, 0) < SAVE_VERSION) return null;

  const meta = isObject(raw.meta) ? raw.meta : {};
  const run = isObject(raw.run) ? raw.run : {};
  const rng = isObject(raw.rngSeed) ? raw.rngSeed : {};

  /**
   * v0.45 (A93) : la *GachaDice v100* n'existe plus. Les d100 relus deviennent
   * des d20 — sauf dans une partie **marquée debug** (A87) qui n'a jamais
   * possédé *Centésimal* : là, un d100 ne peut venir que du forgeur de debug,
   * et il reste un d100 (dé valide du moteur, A93 §2).
   */
  const tainted = meta.tainted === true;
  const hadCentesimal = isObject(raw.tree) && int(raw.tree.shop_v100, 0, 0) > 0;
  const retireD100 = !tainted || hadCentesimal;
  const inventory: MasterDie[] = [];
  if (Array.isArray(raw.inventory)) {
    raw.inventory.forEach((d, i) => {
      const die = normalizeDie(d, i, retireD100);
      if (die) inventory.push(die);
    });
  }

  const known = new Set(inventory.map((d) => d.id));
  const equipped: (string | null)[] = [];
  if (Array.isArray(raw.equipped)) {
    for (const slot of raw.equipped) {
      equipped.push(typeof slot === 'string' && known.has(slot) ? slot : null);
    }
  }
  if (equipped.length === 0) equipped.push(null);

  const seed = int(rng.seed, base.rngSeed.seed, 0) >>> 0;
  return {
    version: SAVE_VERSION,
    meta: {
      // A93 : l'or investi dans *Centésimal* et *Avant-garde* est rendu.
      gold: int(meta.gold, 0, 0) + retiredNodesRefund(raw.tree),
      savedAt: int(meta.savedAt, 0, 0),
      runGold: int(meta.runGold, 0, 0),
      minionEnabled: meta.minionEnabled === true,
      // A87 : une sauvegarde sans identifiant (ou au sien illisible) en reçoit un neuf, sans remise à zéro.
      saveId: readSaveId(meta.saveId) ?? makeSaveId(),
      tainted,
      // A94-13 : compteur permanent des Corrupted GachaDice (additif, `{}`).
      corruptedPurchases: normalizeCorruptedPurchases(meta.corruptedPurchases),
    },
    run: {
      phase: normalizePhase(run.phase),
      level: Math.max(1, int(run.level, 1, 1)),
      day: Math.min(5, Math.max(1, int(run.day, 1, 1))),
      backhoe: int(run.backhoe, 0, 0),
      backhoeArmed: run.backhoeArmed === true,
      guinsoo: normalizeGuinsoo(run.guinsoo, known),
      world: normalizeWorld(run.world),
      bossId: isBossId(run.bossId) ? run.bossId : null,
      // A112 : additif, `false` par défaut (comme `backhoeArmed`).
      gateReached: run.gateReached === true,
    },
    inventory,
    equipped,
    tree: normalizeTree(raw.tree),
    stats: normalizeStats(raw.stats),
    rngSeed: { seed, state: int(rng.state, seed, 0) >>> 0 },
  };
}

/**
 * **Signature de la sauvegarde** (v0.55.10, A121). Le JSON écrit porte un champ
 * `sig` : SHA-256 de `SAVE_SIGNING_KEY + "\n" + JSON du reste`. Une sauvegarde
 * modifiée à la main (or, dés, arbre…) ne correspond plus à sa signature : elle
 * est **chargée quand même**, mais **marquée debug** (`meta.tainted`, collant
 * jusqu'à « Nouvelle partie ») — donc exclue des classements. Une sauvegarde
 * **sans** signature (d'avant cette version, ou un outil qui l'a retirée) est
 * acceptée telle quelle et signée à la prochaine écriture.
 *
 * C'est un **frein**, pas une protection : la clé est dans le code du jeu, un
 * joueur qui le lit peut recalculer la signature. Elle arrête l'édition dans les
 * outils de développement et le partage de sauvegardes retouchées ; le contrôle
 * de plausibilité du Worker (lot suivant d'A121) couvre le reste.
 */
export const SAVE_SIGNING_KEY = 'DiceOrDie·sauvegarde·2026-10-30·3b7e9c1f4a62d8f0';
export const SAVE_SIG_FIELD = 'sig';

/** Signature d'un corps de sauvegarde (le JSON sans `sig`). */
export function saveSignature(body: string): string {
  return sha256Hex(`${SAVE_SIGNING_KEY}\n${body}`);
}

export type SaveSignatureVerdict = 'ok' | 'missing' | 'bad';

/**
 * Vérifie la signature d'un JSON de sauvegarde **déjà analysé** : `ok`, `missing`
 * (pas de champ `sig`) ou `bad` (champ présent mais différent du corps). Le corps
 * est le JSON de l'objet sans `sig`, dans l'ordre de ses clés — celui que
 * `serializeSave` a écrit.
 */
export function verifySaveSignature(parsed: unknown): SaveSignatureVerdict {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return 'missing';
  const o = parsed as Record<string, unknown>;
  if (!(SAVE_SIG_FIELD in o)) return 'missing';
  const { [SAVE_SIG_FIELD]: sig, ...rest } = o;
  if (typeof sig !== 'string') return 'bad';
  return saveSignature(JSON.stringify(rest)) === sig ? 'ok' : 'bad';
}

/** Sérialise une sauvegarde en JSON **signé** (utilitaire pour `storage.ts` et les tests). */
export function serializeSave(save: SaveData): string {
  const { [SAVE_SIG_FIELD]: _dropped, ...body } = save as SaveData & { sig?: unknown };
  const json = JSON.stringify(body);
  // `sig` est ajouté en dernier : le corps signé est exactement `json`.
  return `${json.slice(0, -1)},"${SAVE_SIG_FIELD}":"${saveSignature(json)}"}`;
}

/**
 * Désérialise et valide. Renvoie `null` si le JSON est illisible **ou** si la
 * sauvegarde est antérieure à la version 2 (A8). Une signature **fausse**
 * marque la partie debug (A121) ; une signature absente est tolérée.
 */
export function deserializeSave(json: string): SaveData | null {
  try {
    const parsed: unknown = JSON.parse(json);
    const save = normalizeSave(parsed);
    if (save && verifySaveSignature(parsed) === 'bad') save.meta.tainted = true;
    return save;
  } catch {
    return null;
  }
}

/** Construit la sauvegarde à partir de l'état de jeu et de l'état du Rng. */
export function buildSave(state: GameState, rng: { seed: number; state: number }): SaveData {
  return {
    version: SAVE_VERSION,
    meta: {
      gold: state.gold,
      savedAt: Date.now(),
      runGold: state.runGold,
      minionEnabled: state.minionEnabled === true,
      saveId: state.saveId,
      tainted: state.tainted === true,
      corruptedPurchases: { ...(state.corruptedPurchases ?? {}) },
    },
    run: {
      phase: state.phase,
      level: state.level,
      day: Math.min(BALANCE.daysPerLevel, state.day),
      backhoe: state.backhoe ?? 0,
      backhoeArmed: state.backhoeArmed === true,
      guinsoo: { ...(state.guinsoo ?? {}) },
      world: state.world ? { ...state.world } : null,
      bossId: state.bossId ?? null,
      // A112 : `gateReached` est écrit ; `entitled` ne l'est jamais.
      gateReached: state.gateReached === true,
    },
    inventory: state.inventory.map((d) => ({
      ...d,
      affixes: d.affixes.map((a) => ({ ...a })),
      spells: (d.spells ?? []).slice(),
      ...(d.loaded ? { loaded: { face: d.loaded.face, replaced: d.loaded.replaced.slice() } } : {}),
      // A94 : marques de forge copiées (les autres champs du mode infini sont
      // des scalaires déjà recopiés par la décomposition, s'ils existent).
      ...(d.forge ? { forge: { ...d.forge } } : {}),
    })),
    equipped: state.equipped.slice(),
    tree: { ...state.nodes },
    stats: { ...state.stats },
    rngSeed: { seed: rng.seed, state: rng.state },
  };
}
